import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeftIcon,
  EllipsisVerticalIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline";
import {
  MdOutlineEmail,
  MdOutlinePerson,
  MdOutlinePhoneAndroid
} from "react-icons/md";

import { CollapsibleBottomBar } from "../../atomic/CollapsibleBottombar";
import type { LoanApplicationNavbarData } from "../../../../../src/shared/context/LoanApplicationNavbarContext";
import { getCurrentOrganisation } from "src/shared/utils/Global";
import { ORG_NAMES } from "src/shared/utils/constants";

interface LoanApplicationMobileNavbarProps {
  loanData: LoanApplicationNavbarData;
}

export function LoanApplicationMobileNavbar({
  loanData
}: LoanApplicationMobileNavbarProps) {
  const navigate = useNavigate();
  const [showActionsBottomBar, setShowActionsBottomBar] = useState(false);
  const [showInfoBottomBar, setShowInfoBottomBar] = useState(false);

  const loanActions = loanData?.actions ?? [];

  const hasLoanActions = loanActions?.some((item) => item?.isShow);
  const detailsSidebarData = loanData?.detailsSidebarData;
  const isMFL_LAP = getCurrentOrganisation() === ORG_NAMES.MFL_LAP;

  return (
    <nav className="bg-white sticky top-0 z-[30] w-full border-b border-gray-200">
      <div className="mx-auto max-w-screen-3xl px-4 sm:px-6 lg:px-2">
        <div className="flex h-16 items-center justify-between">
          <button
            onClick={() => {
              if (isMFL_LAP) {
                navigate("/my-queue");
              } else {
                navigate("/leads");
              }
            }}
            className="flex items-center justify-center p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back to My Queue"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>

          <div className="flex-1 flex flex-col justify-center px-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">
                {loanData.applicantName || "N/A"}
              </h1>
              <button
                className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                aria-label="Applicant information"
                onClick={() => setShowInfoBottomBar(true)}
              >
                <InformationCircleIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-0.5">
              {loanData.loanApplicationNum || loanData.loanApplicationID}
            </p>
          </div>

          {hasLoanActions && (
            <button
              type="button"
              className="flex items-center justify-center p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Open action menu"
              onClick={() => setShowActionsBottomBar(true)}
            >
              <EllipsisVerticalIcon className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      {hasLoanActions && (
        <CollapsibleBottomBar
          title="Actions"
          show={showActionsBottomBar}
          setShow={setShowActionsBottomBar}
        >
          <div className="mt-4 space-y-2">
            {loanActions
              ?.filter((item) => item?.isShow)
              .map((item, index) => (
                <div
                  key={index}
                  className="py-1"
                  onClick={() => setShowActionsBottomBar(false)}
                >
                  {item.value}
                </div>
              ))}
          </div>
        </CollapsibleBottomBar>
      )}

      {detailsSidebarData && (
        <CollapsibleBottomBar
          title="Applicant Details"
          show={showInfoBottomBar}
          setShow={setShowInfoBottomBar}
        >
          <div className="mt-4 space-y-4">
            {/* Profile Section */}
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full overflow-hidden mb-3">
                {detailsSidebarData.selfie ? (
                  <img
                    src={detailsSidebarData.selfie}
                    alt="user_selfie"
                    className="w-full h-full aspect-auto"
                  />
                ) : (
                  <MdOutlinePerson className="w-16 h-16 aspect-auto text-gray-400" />
                )}
              </div>

              {detailsSidebarData.config && (
                <div className="flex items-center justify-center mb-3">
                  <p
                    className="font-medium max-w-max text-xs capitalize px-3 py-1.5 rounded-md text-center"
                    style={{
                      color: detailsSidebarData.config?.["color"],
                      backgroundColor: detailsSidebarData.config?.["bg"]
                    }}
                  >
                    {detailsSidebarData.config?.label}
                  </p>
                </div>
              )}

              <p className="text-center text-sm font-medium mb-2">
                {detailsSidebarData.details?.name}
              </p>

              <p className="text-center text-xs text-gray-400 mb-1 flex items-center justify-center">
                <MdOutlinePhoneAndroid className="h-4 w-4 mr-1" />
                {detailsSidebarData.details?.mobile}
              </p>

              {detailsSidebarData.details?.email && (
                <p className="text-center text-xs text-gray-400 flex items-center justify-center mb-1">
                  <MdOutlineEmail className="h-4 w-4 mr-1" />
                  {detailsSidebarData.details?.email}
                </p>
              )}

              {detailsSidebarData.isMFLLAP ? (
                <p className="text-center text-xs text-gray-400 mb-4 capitalize">
                  Loan Against Property
                </p>
              ) : (
                detailsSidebarData.details?.loanType && (
                  <p className="text-center text-xs text-gray-400 mb-4 capitalize">
                    {detailsSidebarData.details?.loanType?.replace(/_/g, " ")}
                  </p>
                )
              )}
            </div>

            {/* Details Section */}
            <div className="space-y-3">
              {detailsSidebarData.items?.map((item, itemIdx) => {
                const shouldHideAgentField =
                  detailsSidebarData.isMFLLAP &&
                  item?.name?.toLowerCase().includes("agent");

                if (shouldHideAgentField) return null;

                return (
                  <div
                    key={itemIdx}
                    className="flex justify-between items-start"
                  >
                    <p className="text-xs text-gray-500 font-medium flex-1">
                      {item.name}
                    </p>
                    <p className="text-xs text-black flex-1 text-right">
                      {item.value}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </CollapsibleBottomBar>
      )}
    </nav>
  );
}
